import {Theme} from '@mui/material';

import {Formatters} from './light/LightFormatter';

/**
 * Shared resolver for the medication "usage type" chip used both in the MERIE
 * timeline annotation tooltip ({@link MedicationAnnotation}) and in the
 * structured medications placeholder table ({@link MedicationPlaceholderDocument}).
 *
 * Color scheme (matches RISKINESS_COLORS.{LOW,MEDIUM}.hexDarker — same
 * saturated pastel shades used for "Low/Medium" risk chips throughout the app):
 *   - ACUTE   → green  (#A7F0BA)
 *   - CHRONIC → yellow (#FDDC69)
 *   - UNKNOWN → light gray (cool gray 200) — used only by the placeholder
 *               table, never in the timeline tooltip (where UNKNOWN renders
 *               no chip at all).
 *   - DISCONTINUED → cool gray 600 (overrides usageType when isDiscontinued).
 *
 * Decision policy ({@link resolveMedicationUsageTypeChip}):
 *   - If `isDiscontinued` is true → "Discontinued" chip in dark gray.
 *   - Else if `usageType` is ACUTE or CHRONIC → matching colored chip.
 *   - Else (UNKNOWN / undefined / other) → either no chip (timeline) or a
 *     light-gray "Unknown" chip (table), controlled by the `forceShow` flag.
 */

export type MedicationUsageType = 'ACUTE' | 'CHRONIC' | 'UNKNOWN' | string | null | undefined;

export interface MedicationUsageTypeChipParams {
  usageType: MedicationUsageType;
  isDiscontinued: boolean | null | undefined;
  formatters: Formatters;
  theme: Theme;
  /**
   * When true, returns a light-gray "Unknown" chip for UNKNOWN/empty
   * usageType. When false (default), returns null in that case — the caller
   * then omits the chip entirely. The MERIE timeline uses `false`; the
   * placeholder table uses `true`.
   */
  forceShow?: boolean;
  /**
   * When true (default), `isDiscontinued` overrides usageType and renders the
   * dark-gray "Discontinued" chip. When false, the chip always reflects the
   * raw backend usageType (ACUTE / CHRONIC / UNKNOWN) with its own color — the
   * discontinued state is then conveyed elsewhere (e.g. grayed row text). The
   * MERIE timeline tooltip uses `true`; the placeholder table uses `false`.
   */
  discontinuedOverridesUsageType?: boolean;
}

export interface MedicationUsageTypeChipDescriptor {
  label: string;
  backgroundColor: string;
  /** When true, render with white text (DlMuiChip `whiteText` prop). */
  whiteText: boolean;
}

// Saturated pastel palette — matches RISKINESS_COLORS.LOW.hexDarker and
// RISKINESS_COLORS.MEDIUM.hexDarker (defined in packages/core/src/js/global_constants.ts)
// and the same shades are used in HospitalizationDocument color list.
const ACUTE_GREEN = '#A7F0BA';
const CHRONIC_YELLOW = '#FDDC69';

export function resolveMedicationUsageTypeChip(
  params: MedicationUsageTypeChipParams
): MedicationUsageTypeChipDescriptor | null {
  const {usageType, isDiscontinued, formatters, theme, forceShow = false, discontinuedOverridesUsageType = true} = params;
  const {t} = formatters;

  if (isDiscontinued && discontinuedOverridesUsageType) {
    return {
      label: t('timeline.annotationTooltip.usageType.isDiscontinued'),
      backgroundColor: theme.palette.custom.coolGray['600'],
      whiteText: true,
    };
  }
  if (usageType === 'ACUTE') {
    return {
      label: t('timeline.annotationTooltip.usageType.acute'),
      backgroundColor: ACUTE_GREEN,
      whiteText: false,
    };
  }
  if (usageType === 'CHRONIC') {
    return {
      label: t('timeline.annotationTooltip.usageType.chronic'),
      backgroundColor: CHRONIC_YELLOW,
      whiteText: false,
    };
  }
  if (forceShow) {
    return {
      label: t('global.unknown'),
      backgroundColor: theme.palette.custom.coolGray['200'],
      whiteText: false,
    };
  }
  return null;
}
